import React from 'react';
import { Link } from 'react-router-dom';
import { CircleDot, Waves, Music, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import './Home.css';

const Home = () => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Cosmergence",
    "url": baseUrl,
    "logo": `${baseUrl}/logo.png`,
    "description": "A philosophy of eternal resonance bridging science and spirituality",
    "sameAs": []
  };

  const coreBeliefs = [
    {
      icon: <CircleDot size={40} />,
      title: 'Continuity',
      description: 'Nothing is created or destroyed; everything transforms.'
    },
    {
      icon: <Music size={40} />,
      title: 'Conscious Resonance',
      description: 'Awareness is a patterned vibration—our life\'s song.'
    },
    {
      icon: <Waves size={40} />,
      title: 'Harmony',
      description: 'Living well means tuning our actions toward balance and kindness.'
    }
  ];

  return (
    <div className="home-page">
      <SEO
        title="A Philosophy of Eternal Resonance"
        description="We are temporary patterns of matter-energy—songs in a single cosmos. Explore a science-based philosophy that reconciles life, death, and cosmic consciousness without religion."
        keywords="cosmic philosophy, secular spirituality, science-based philosophy, what happens after death, conscious resonance, energy transformation, non-religious philosophy, spiritual but not religious"
        canonicalUrl="/"
        structuredData={structuredData}
      />
      
      {/* Hero Section */}
      <section className="hero cosmic-bg">
        <div className="river-flow"></div>
        <div className="container hero-content">
          <h1 className="hero-large fade-in">
            Cosmergence: A Philosophy of Eternal Resonance
          </h1>
          <p className="hero-medium fade-in">
            We are temporary patterns of matter‑energy—songs in a single cosmos.
          </p>
          <div className="hero-cta fade-in">
            <Link to="/beliefs" className="btn-primary">
              Explore the Belief
              <ArrowRight size={18} />
            </Link>
            <Link to="/prayer" className="btn-secondary">
              Return to Resonance
            </Link>
          </div>
        </div>
      </section>

      {/* What We Believe Section */}
      <section className="section-padding">
        <div className="container">
          <h2 className="heading-1 text-center mb-8">What We Believe</h2>
          <div className="grid-3">
            {coreBeliefs.map((belief, index) => (
              <div key={index} className="belief-card card hover-lift">
                <div className="belief-icon text-gold">
                  {belief.icon}
                </div>
                <h3 className="heading-3">{belief.title}</h3>
                <p className="body-regular">{belief.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pull Quote Section */}
      <section className="section-padding-small">
        <div className="container">
          <div className="quote-callout">
            <p>"Nothing is lost. Live in tune. Return with gratitude."</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding cosmic-bg">
        <div className="container">
          <div className="cta-section text-center">
            <h2 className="heading-1 mb-4">Explore the Philosophy</h2>
            <p className="body-large mb-4" style={{maxWidth: '600px', margin: '0 auto 32px'}}>
              Discover the seven tenets that guide our understanding of existence, consciousness, and the cosmic flow.
            </p>
            <Link to="/codex" className="btn-primary">
              Read the Cosmergence Codex
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;